import { TypedEventEmitter, setMaxListeners, start, stop } from '@libp2p/interface';
import { AdaptiveTimeout } from '@libp2p/utils/adaptive-timeout';
import { PeerQueue } from '@libp2p/utils/peer-queue';
import { anySignal } from 'any-signal';
import parallel from 'it-parallel';
import * as utils from '../utils.js';
import { ClosestPeers } from './closest-peers.js';
import { KBucket, isLeafBucket } from './k-bucket.js';
export const KBUCKET_SIZE = 20;
export const PREFIX_LENGTH = 6;
export const PING_NEW_CONTACT_TIMEOUT = 2000;
export const PING_NEW_CONTACT_CONCURRENCY = 20;
export const PING_NEW_CONTACT_MAX_QUEUE_SIZE = 100;
export const PING_OLD_CONTACT_COUNT = 3;
export const PING_OLD_CONTACT_TIMEOUT = 2000;
export const PING_OLD_CONTACT_CONCURRENCY = 20;
export const PING_OLD_CONTACT_MAX_QUEUE_SIZE = 100;
export const KAD_PEER_TAG_NAME = 'kad-peer';
export const KAD_PEER_TAG_VALUE = 1;
export const LAST_PING_THRESHOLD = 600000;
export const POPULATE_FROM_DATASTORE_ON_START = true;
export const POPULATE_FROM_DATASTORE_LIMIT = 1000;
/**
 * A wrapper around `k-bucket`, to provide easy store and retrieval for peers.
 */
export class RoutingTable extends TypedEventEmitter {
    kBucketSize;
    kb;
    network;
    closestPeerTagger;
    log;
    components;
    running;
    pingNewContactTimeout;
    pingNewContactQueue;
    pingOldContactTimeout;
    pingOldContactQueue;
    populateFromDatastoreOnStart;
    populateFromDatastoreLimit;
    protocol;
    peerTagName;
    peerTagValue;
    metrics;
    constructor(components, init) {
        super();
        this.components = components;
        this.log = components.logger.forComponent(`${init.logPrefix}:routing-table`);
        this.kBucketSize = init.kBucketSize ?? KBUCKET_SIZE;
        this.running = false;
        this.protocol = init.protocol;
        this.network = init.network;
        this.peerTagName = init.peerTagName ?? KAD_PEER_TAG_NAME;
        this.peerTagValue = init.peerTagValue ?? KAD_PEER_TAG_VALUE;
        this.pingOldContacts = this.pingOldContacts.bind(this);
        this.verifyNewContact = this.verifyNewContact.bind(this);
        this.peerAdded = this.peerAdded.bind(this);
        this.peerRemoved = this.peerRemoved.bind(this);
        this.populateFromDatastoreOnStart = init.populateFromDatastoreOnStart ?? POPULATE_FROM_DATASTORE_ON_START;
        this.populateFromDatastoreLimit = init.populateFromDatastoreLimit ?? POPULATE_FROM_DATASTORE_LIMIT;
        this.pingOldContactQueue = new PeerQueue({
            concurrency: init.pingOldContactConcurrency ?? PING_OLD_CONTACT_CONCURRENCY,
            metricName: `${init.metricsPrefix}_ping_old_contact_queue`,
            metrics: this.components.metrics,
            maxSize: init.pingOldContactMaxQueueSize ?? PING_OLD_CONTACT_MAX_QUEUE_SIZE
        });
        this.pingOldContactTimeout = new AdaptiveTimeout({
            ...(init.pingOldContactTimeout ?? {}),
            metrics: this.components.metrics,
            metricName: `${init.metricsPrefix}_routing_table_ping_old_contact_time_milliseconds`
        });
        this.pingNewContactQueue = new PeerQueue({
            concurrency: init.pingNewContactConcurrency ?? PING_NEW_CONTACT_CONCURRENCY,
            metricName: `${init.metricsPrefix}_ping_new_contact_queue`,
            metrics: this.components.metrics,
            maxSize: init.pingNewContactMaxQueueSize ?? PING_NEW_CONTACT_MAX_QUEUE_SIZE
        });
        this.pingNewContactTimeout = new AdaptiveTimeout({
            ...(init.pingNewContactTimeout ?? {}),
            metrics: this.components.metrics,
            metricName: `${init.metricsPrefix}_routing_table_ping_new_contact_time_milliseconds`
        });
        this.kb = new KBucket({
            kBucketSize: init.kBucketSize,
            prefixLength: init.prefixLength,
            splitThreshold: init.splitThreshold,
            numberOfOldContactsToPing: init.numberOfOldContactsToPing,
            lastPingThreshold: init.lastPingThreshold,
            ping: this.pingOldContacts,
            verify: this.verifyNewContact,
            onAdd: this.peerAdded,
            onRemove: this.peerRemoved
        });
        this.closestPeerTagger = new ClosestPeers(this.components, {
            logPrefix: init.logPrefix,
            routingTable: this,
            peerSetSize: init.closestPeerSetSize,
            refreshInterval: init.closestPeerSetRefreshInterval,
            closeTagName: init.closeTagName,
            closeTagValue: init.closeTagValue
        });
        if (this.components.metrics != null) {
            this.metrics = {
                routingTableSize: this.components.metrics.registerMetric(`${init.metricsPrefix}_routing_table_size`),
                routingTableKadBucketTotal: this.components.metrics.registerMetric(`${init.metricsPrefix}_routing_table_kad_bucket_total`),
                routingTableKadBucketAverageOccupancy: this.components.metrics.registerMetric(`${init.metricsPrefix}_routing_table_kad_bucket_average_occupancy`),
                routingTableKadBucketMinOccupancy: this.components.metrics.registerMetric(`${init.metricsPrefix}_routing_table_kad_bucket_min_occupancy`),
                routingTableKadBucketMaxOccupancy: this.components.metrics.registerMetric(`${init.metricsPrefix}_routing_table_kad_bucket_max_occupancy`),
                routingTableKadBucketMaxDepth: this.components.metrics.registerMetric(`${init.metricsPrefix}_routing_table_kad_bucket_max_depth`),
                kadBucketEvents: this.components.metrics.registerCounterGroup(`${init.metricsPrefix}_kad_bucket_events_total`)
            };
        }
    }
    isStarted() {
        return this.running;
    }
    async start() {
        if (this.running) {
            return;
        }
        this.running = true;
        await start(this.closestPeerTagger);
        await this.kb.addSelfPeer(this.components.peerId);
    }
    async afterStart() {
        // do this async to not block startup but iterate serially to not overwhelm
        // the ping queue
        Promise.resolve().then(async () => {
            if (!this.populateFromDatastoreOnStart) {
                return;
            }
            let peerStorePeers = 0;
            // add existing peers from the peer store to routing table
            for (const peer of await this.components.peerStore.all({
                filters: [(peer) => {
                        return peer.protocols.includes(this.protocol) && peer.tags.has(KAD_PEER_TAG_NAME);
                    }],
                limit: this.populateFromDatastoreLimit
            })) {
                if (!this.running) {
                    // bail if we've been shut down
                    return;
                }
                try {
                    await this.add(peer.id);
                    peerStorePeers++;
                }
                catch (err) {
                    this.log('failed to add peer %p to routing table, removing kad-dht peer tags - %e');
                    await this.components.peerStore.merge(peer.id, {
                        tags: {
                            [this.peerTagName]: undefined
                        }
                    });
                }
            }
            this.log('added %d peer store peers to the routing table', peerStorePeers);
        })
            .catch(err => {
            this.log.error('error adding peer store peers to the routing table %e', err);
        });
    }
    async stop() {
        this.running = false;
        await stop(this.closestPeerTagger);
        this.pingOldContactQueue.abort();
        this.pingNewContactQueue.abort();
    }
    async peerAdded(peer, bucket) {
        if (!this.components.peerId.equals(peer.peerId)) {
            await this.components.peerStore.merge(peer.peerId, {
                tags: {
                    [this.peerTagName]: {
                        value: this.peerTagValue
                    }
                }
            });
        }
        this.updateMetrics();
        this.metrics?.kadBucketEvents.increment({ peer_added: true });
        this.safeDispatchEvent('peer:add', { detail: peer.peerId });
    }
    async peerRemoved(peer, bucket) {
        if (!this.components.peerId.equals(peer.peerId)) {
            await this.components.peerStore.merge(peer.peerId, {
                tags: {
                    [this.peerTagName]: undefined
                }
            });
        }
        this.updateMetrics();
        this.metrics?.kadBucketEvents.increment({ peer_removed: true });
        this.safeDispatchEvent('peer:remove', { detail: peer.peerId });
    }
    /**
     * Called on the `ping` event from `k-bucket` when a bucket is full
     * and cannot split.
     *
     * `oldContacts.length` is defined by the `numberOfNodesToPing` param
     * passed to the `k-bucket` constructor.
     *
     * `oldContacts` will not be empty and is the list of contacts that
     * have not been contacted for the longest.
     */
    async *pingOldContacts(oldContacts, options) {
        if (!this.running) {
            return;
        }
        const jobs = [];
        for (const oldContact of oldContacts) {
            if (this.kb.get(oldContact.kadId) == null) {
                this.log('asked to ping contact %p that was not in routing table', oldContact.peerId);
                continue;
            }
            this.metrics?.kadBucketEvents.increment({ ping_old_contact: true });
            jobs.push(async () => {
                // if a previous ping wants us to ping this contact, re-use the result
                const existingJob = this.pingOldContactQueue.find(oldContact.peerId);
                if (existingJob != null) {
                    this.log('asked to ping contact %p was already being pinged', oldContact.peerId);
                    const result = await existingJob.join(options);
                    if (!result) {
                        return oldContact;
                    }
                    return;
                }
                const result = await this.pingOldContactQueue.add(async (options) => {
                    const signal = this.pingOldContactTimeout.getTimeoutSignal();
                    const signals = anySignal([signal, options?.signal]);
                    setMaxListeners(Infinity, signal, signals);
                    try {
                        return await this.pingContact(oldContact, options);
                    }
                    catch {
                        this.metrics?.kadBucketEvents.increment({ ping_old_contact_error: true });
                        return true;
                    }
                    finally {
                        this.pingOldContactTimeout.cleanUp(signal);
                        signals.clear();
                    }
                }, {
                    peerId: oldContact.peerId,
                    signal: options?.signal
                });
                if (!result) {
                    return oldContact;
                }
            });
        }
        for await (const peer of parallel(jobs)) {
            if (peer != null) {
                yield peer;
            }
        }
    }
    async verifyNewContact(contact, options) {
        const signal = this.pingNewContactTimeout.getTimeoutSignal();
        const signals = anySignal([signal, options?.signal]);
        setMaxListeners(Infinity, signal, signals);
        try {
            const job = this.pingNewContactQueue.find(contact.peerId);
            if (job != null) {
                this.log('joining existing ping to add new peer %p to routing table', contact.peerId);
                return await job.join({
                    signal: signals
                });
            }
            else {
                return await this.pingNewContactQueue.add(async (options) => {
                    this.metrics?.kadBucketEvents.increment({ ping_new_contact: true });
                    this.log('pinging new peer %p before adding to routing table', contact.peerId);
                    return this.pingContact(contact, options);
                }, {
                    peerId: contact.peerId,
                    signal: signals
                });
            }
        }
        catch (err) {
            this.log.trace('tried to add peer %p but they were not online', contact.peerId);
            this.metrics?.kadBucketEvents.increment({ ping_new_contact_error: true });
            return false;
        }
        finally {
            this.pingNewContactTimeout.cleanUp(signal);
            signals.clear();
        }
    }
    async pingContact(contact, options) {
        let stream;
        try {
            this.log('pinging contact %p', contact.peerId);
            await this.components.ping.ping(contact.peerId, options);
            this.log('contact %p ping ok', contact.peerId);
            this.safeDispatchEvent('peer:ping', {
                detail: contact.peerId
            });
            return true;
        }
        catch (err) {
            this.log('error pinging old contact %p - %e', contact.peerId, err);
            stream?.abort(err);
            return false;
        }
    }
    /**
     * Amount of currently stored peers
     */
    get size() {
        if (this.kb == null) {
            return 0;
        }
        return this.kb.count();
    }
    /**
     * Find a specific peer by id
     */
    async find(peer) {
        const kadId = await utils.convertPeerId(peer);
        return this.kb.get(kadId)?.peerId;
    }
    /**
     * Retrieve the closest peers to the given kadId
     */
    closestPeer(kadId) {
        const res = this.closestPeers(kadId, 1);
        if (res.length > 0) {
            return res[0];
        }
        return undefined;
    }
    /**
     * Retrieve the `count`-closest peers to the given kadId
     */
    closestPeers(kadId, count = this.kBucketSize) {
        if (this.kb == null) {
            return [];
        }
        return [...this.kb.closest(kadId, count)];
    }
    /**
     * Add or update the routing table with the given peer
     */
    async add(peerId, options) {
        if (this.kb == null) {
            throw new Error('RoutingTable is not started');
        }
        await this.kb.add(peerId, options);
    }
    /**
     * Remove a given peer from the table
     */
    async remove(peer) {
        if (this.kb == null) {
            throw new Error('RoutingTable is not started');
        }
        const kadId = await utils.convertPeerId(peer);
        await this.kb.remove(kadId);
    }
    updateMetrics() {
        if (this.metrics == null || this.kb == null) {
            return;
        }
        let size = 0;
        let buckets = 0;
        let maxDepth = 0;
        let minOccupancy = 20;
        let maxOccupancy = 0;
        function count(bucket) {
            if (isLeafBucket(bucket)) {
                if (bucket.depth > maxDepth) {
                    maxDepth = bucket.depth;
                }
                buckets++;
                size += bucket.peers.length;
                if (bucket.peers.length < minOccupancy) {
                    minOccupancy = bucket.peers.length;
                }
                if (bucket.peers.length > maxOccupancy) {
                    maxOccupancy = bucket.peers.length;
                }
                return;
            }
            count(bucket.left);
            count(bucket.right);
        }
        count(this.kb.root);
        this.metrics.routingTableSize.update(size);
        this.metrics.routingTableKadBucketTotal.update(buckets);
        this.metrics.routingTableKadBucketAverageOccupancy.update(Math.round(size / buckets));
        this.metrics.routingTableKadBucketMinOccupancy.update(minOccupancy);
        this.metrics.routingTableKadBucketMaxOccupancy.update(maxOccupancy);
        this.metrics.routingTableKadBucketMaxDepth.update(maxDepth);
    }
}
//# sourceMappingURL=index.js.map